import { useState, useRef, useEffect } from 'react'
import { useDrag } from 'react-dnd'
import { Icon } from '@/components/ui/icon'
import { MenuIcon } from '@/components/workspace/context-menu/MenuIcon'
import { toast } from '@/components/ui/toast'
import { cn } from '@/lib/utils'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import type { LoraListItem } from '@/hooks/useUserLoras'
import type { LoraFolder } from '@/lib/api/training'

// Drag type for LoRA models dropped onto canvas
export const LORA_MODEL_DRAG_TYPE = 'lora_model'

// ── Draggable Model Item ──────────────────────────────────────────

interface DraggableModelItemProps {
  model: LoraListItem
  folders: LoraFolder[]
  isRenaming: boolean
  renameValue: string
  onRenameValueChange: (value: string) => void
  onRenameSubmit: () => void
  onRenameCancel: () => void
  onStartRename: () => void
  onDelete: () => void
  onMoveToFolder: (folderId: string | null) => void
}

function DraggableModelItem({
  model,
  folders,
  isRenaming,
  renameValue,
  onRenameValueChange,
  onRenameSubmit,
  onRenameCancel,
  onStartRename,
  onDelete,
  onMoveToFolder,
}: DraggableModelItemProps) {
  const renameInputRef = useRef<HTMLInputElement>(null)
  const [{ isDragging }, drag] = useDrag({
    type: LORA_MODEL_DRAG_TYPE,
    item: {
      id: model.id,
      name: model.name,
      triggerWord: model.triggerWord,
      baseModel: model.baseModel,
      thumbnailUrl: model.thumbnailUrl,
    },
    canDrag: model.status === 'ready' && !isRenaming,
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  })

  useEffect(() => {
    if (isRenaming) {
      renameInputRef.current?.focus()
      renameInputRef.current?.select()
    }
  }, [isRenaming])

  const statusIcon = model.status === 'ready'
    ? null
    : model.status === 'training'
      ? 'loader-2'
      : 'alert-circle'

  const statusColor = model.status === 'ready'
    ? 'text-emerald-500'
    : model.status === 'training'
      ? 'text-amber-500'
      : 'text-destructive'

  // Folders this model can move to (exclude current folder)
  const moveTargets = model.folderId
    ? [{ id: null, name: 'Root' }, ...folders.filter((f) => f.id !== model.folderId)]
    : folders

  return (
    <div
      ref={model.status === 'ready' && !isRenaming ? drag as unknown as React.LegacyRef<HTMLDivElement> : undefined}
      className={cn(
        'flex items-center gap-3 py-2 px-3 rounded-md border border-border bg-card hover:border-foreground/20 transition-colors group',
        model.status === 'ready' && !isRenaming && 'cursor-grab active:cursor-grabbing',
        model.status !== 'ready' && 'opacity-70',
        isDragging && 'opacity-50',
      )}
    >
      {/* Thumbnail */}
      <div className="h-10 w-10 rounded-md bg-muted flex-shrink-0 overflow-hidden flex items-center justify-center">
        {model.thumbnailUrl ? (
          <img src={model.thumbnailUrl} alt={model.name} className="h-full w-full object-cover" />
        ) : (
          <Icon name="box" className="h-5 w-5 text-muted-foreground opacity-40" />
        )}
      </div>

      {/* Info */}
      <div className="flex flex-col flex-1 min-w-0">
        {isRenaming ? (
          <input
            ref={renameInputRef}
            type="text"
            value={renameValue}
            onChange={(e) => onRenameValueChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onRenameSubmit()
              if (e.key === 'Escape') onRenameCancel()
            }}
            onBlur={onRenameSubmit}
            className="bg-transparent text-sm font-medium outline-none border-b border-border w-full"
          />
        ) : (
          <span className="text-sm font-medium truncate">{model.name}</span>
        )}
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className="truncate">{model.baseModel}</span>
          {model.triggerWord && (
            <>
              <span>&middot;</span>
              <span className="truncate font-mono">{model.triggerWord}</span>
            </>
          )}
        </div>
      </div>

      {/* Status badge */}
      {statusIcon && (
        <div className={cn('flex-shrink-0', statusColor)} title={model.status}>
          <Icon
            name={statusIcon}
            className={cn('h-4 w-4', model.status === 'training' && 'animate-spin')}
          />
        </div>
      )}

      {/* Context menu */}
      {!isRenaming && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex h-5 w-5 items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
              onClick={(e) => e.stopPropagation()}
            >
              <Icon name="more-horizontal" className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            {model.status === 'ready' && (
              <DropdownMenuItem onClick={onStartRename}>
                <MenuIcon name="pencil" className="h-4 w-4 mr-2" />
                Rename
              </DropdownMenuItem>
            )}
            {model.status === 'ready' && moveTargets.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                    <MenuIcon name="folder-input" className="h-4 w-4 mr-2" />
                    Move to...
                    <Icon name="chevron-right" className="h-3 w-3 ml-auto" />
                  </DropdownMenuItem>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="right" className="w-40">
                  {moveTargets.map((target) => (
                    <DropdownMenuItem
                      key={target.id ?? 'root'}
                      onClick={() => onMoveToFolder(target.id)}
                    >
                      <MenuIcon name={target.id ? 'folder' : 'home'} className="h-4 w-4 mr-2" />
                      {target.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {model.status === 'ready' && <DropdownMenuSeparator />}
            <DropdownMenuItem
              onClick={onDelete}
              className="text-destructive focus:text-destructive"
            >
              <MenuIcon name="trash-2" className="h-4 w-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}

// ── Models Tab Content ────────────────────────────────────────────

export interface ModelsTabContentProps {
  models: LoraListItem[]
  folders: LoraFolder[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  renameLora: (id: string, name: string) => Promise<boolean>
  deleteLora: (id: string) => Promise<boolean>
  moveLoraToFolder: (loraId: string, folderId: string | null) => Promise<boolean>
  createFolder: (name: string) => Promise<LoraFolder | null>
  renameFolder: (id: string, name: string) => Promise<boolean>
  deleteFolder: (id: string) => Promise<boolean>
}

export function ModelsTabContent({
  models, folders, loading, error, refetch,
  renameLora, deleteLora, moveLoraToFolder,
  createFolder, renameFolder, deleteFolder,
}: ModelsTabContentProps) {
  const listRef = useRef<HTMLDivElement>(null)

  // Action bar state
  const [searchQuery, setSearchQuery] = useState('')
  const [showSearch, setShowSearch] = useState(false)
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest' | 'name' | 'type'>('newest')

  // Folder navigation state
  const [openFolderId, setOpenFolderId] = useState<string | null>(null)
  const openFolder = folders.find((f) => f.id === openFolderId) ?? null

  // Folder inline creation state
  const [isCreatingFolder, setIsCreatingFolder] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const newFolderInputRef = useRef<HTMLInputElement>(null)

  // Folder rename state
  const [renamingFolderId, setRenamingFolderId] = useState<string | null>(null)
  const [renameFolderName, setRenameFolderName] = useState('')
  const renameFolderInputRef = useRef<HTMLInputElement>(null)

  // Model rename state
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')

  // Reset openFolderId if the folder was deleted
  useEffect(() => {
    if (openFolderId && !folders.some((f) => f.id === openFolderId)) {
      setOpenFolderId(null)
    }
  }, [openFolderId, folders])

  // Prevent wheel events from reaching React Flow
  useEffect(() => {
    const el = listRef.current
    if (!el) return
    const handleWheel = (e: WheelEvent) => {
      const canScrollDown = el.scrollTop < el.scrollHeight - el.clientHeight
      const canScrollUp = el.scrollTop > 0
      if ((e.deltaY > 0 && canScrollDown) || (e.deltaY < 0 && canScrollUp)) {
        e.stopPropagation()
      }
    }
    el.addEventListener('wheel', handleWheel)
    return () => el.removeEventListener('wheel', handleWheel)
  }, [])

  // Focus inputs when shown
  useEffect(() => {
    if (isCreatingFolder) newFolderInputRef.current?.focus()
  }, [isCreatingFolder])

  useEffect(() => {
    if (renamingFolderId) {
      renameFolderInputRef.current?.focus()
      renameFolderInputRef.current?.select()
    }
  }, [renamingFolderId])

  // ── Model actions ──────────────────────────────────────────────

  const handleStartRename = (model: LoraListItem) => {
    setRenamingId(model.id)
    setRenameValue(model.name)
  }

  const handleRenameSubmit = async () => {
    if (!renamingId) return
    const trimmed = renameValue.trim()
    if (trimmed) {
      await renameLora(renamingId, trimmed)
    }
    setRenamingId(null)
  }

  const handleRenameCancel = () => {
    setRenamingId(null)
  }

  const handleDelete = async (id: string) => {
    const success = await deleteLora(id)
    if (!success) {
      toast.error('Failed to delete model')
    }
  }

  // ── Folder actions ─────────────────────────────────────────────

  const handleCreateFolder = async () => {
    const name = newFolderName.trim()
    if (!name) return
    const result = await createFolder(name)
    if (result) {
      setNewFolderName('')
      setIsCreatingFolder(false)
    }
  }

  const startFolderRename = (folder: LoraFolder) => {
    setRenamingFolderId(folder.id)
    setRenameFolderName(folder.name)
  }

  const handleFolderRenameSubmit = async (folderId: string) => {
    const name = renameFolderName.trim()
    if (name) {
      await renameFolder(folderId, name)
    }
    setRenamingFolderId(null)
  }

  // ── Filtering & sorting ────────────────────────────────────────

  const filteredModels = models
    .filter((m) => openFolderId ? m.folderId === openFolderId : (m.folderId === null || m.folderId === undefined))
    .filter((m) => !searchQuery || m.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      if (sortOrder === 'name') return a.name.localeCompare(b.name)
      if (sortOrder === 'type') return a.modelType.localeCompare(b.modelType)
      if (sortOrder === 'oldest') return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    })

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Icon name="loader-2" className="h-4 w-4 animate-spin" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground px-4">
        <Icon name="alert-circle" className="h-8 w-8 mb-2 opacity-40" />
        <p className="text-sm font-medium">Failed to load models</p>
        <p className="text-xs mt-1 opacity-70">{error}</p>
        <button
          onClick={() => void refetch()}
          className="mt-3 text-xs text-foreground underline hover:no-underline"
        >
          Try again
        </button>
      </div>
    )
  }

  return (
    <div className="flex flex-col min-h-0 overflow-hidden flex-1">
      {/* Fixed top section: actions + search */}
      <div className="flex-shrink-0">
        {/* Actions Bar */}
        <div className="flex items-center gap-1 px-2 pt-2 pb-3">
          <button
            className={cn(
              "flex h-9 flex-1 items-center justify-center rounded-lg bg-muted hover:bg-accent transition-colors",
              showSearch && "bg-accent"
            )}
            title="Search"
            onClick={() => setShowSearch((v) => !v)}
          >
            <Icon name="search" className="h-5 w-5" />
          </button>
          <button
            className="flex h-9 flex-1 items-center justify-center rounded-lg bg-muted hover:bg-accent transition-colors"
            title="New folder"
            onClick={() => { setIsCreatingFolder(true); setNewFolderName('') }}
          >
            <Icon name="folder-plus" className="h-5 w-5" />
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex h-9 flex-1 items-center justify-center rounded-lg bg-muted hover:bg-accent transition-colors" title="Sort">
                <Icon name="arrow-up-down" className="h-5 w-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-36">
              <DropdownMenuItem onClick={() => setSortOrder('newest')}>
                {sortOrder === 'newest' && <MenuIcon name="check" className="h-4 w-4 mr-2" />}
                {sortOrder !== 'newest' && <span className="w-6" />}
                Newest first
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortOrder('oldest')}>
                {sortOrder === 'oldest' && <MenuIcon name="check" className="h-4 w-4 mr-2" />}
                {sortOrder !== 'oldest' && <span className="w-6" />}
                Oldest first
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortOrder('name')}>
                {sortOrder === 'name' && <MenuIcon name="check" className="h-4 w-4 mr-2" />}
                {sortOrder !== 'name' && <span className="w-6" />}
                By name
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortOrder('type')}>
                {sortOrder === 'type' && <MenuIcon name="check" className="h-4 w-4 mr-2" />}
                {sortOrder !== 'type' && <span className="w-6" />}
                By type
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Search input */}
        {showSearch && (
          <div className="px-2 pb-2">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted">
              <Icon name="search" className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search models..."
                className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                autoFocus
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="text-muted-foreground hover:text-foreground">
                  <Icon name="x" className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Scrollable models list */}
      <div
        ref={listRef}
        className="flex-1 min-h-0 overflow-y-auto px-2 pt-2 pb-2 space-y-2
          [&::-webkit-scrollbar]:w-[6px] [&::-webkit-scrollbar]:bg-transparent
          [&::-webkit-scrollbar-thumb]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full
          hover:[&::-webkit-scrollbar-thumb]:bg-neutral-300 dark:hover:[&::-webkit-scrollbar-thumb]:bg-neutral-600
          [&::-webkit-scrollbar-track]:bg-transparent"
      >
        {/* Folder breadcrumb / back button */}
        {openFolder && (
          <div className="flex items-center gap-2 py-1.5 px-1 mb-1">
            <button
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => setOpenFolderId(null)}
            >
              <Icon name="chevron-left" className="h-4 w-4" />
              <span>Back</span>
            </button>
            <span className="text-sm text-muted-foreground">/</span>
            <div className="flex items-center gap-1.5 min-w-0">
              <Icon name="folder" className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm font-medium truncate">{openFolder.name}</span>
            </div>
          </div>
        )}

        {/* Inline New Folder Input */}
        {isCreatingFolder && !openFolderId && (
          <div className="flex items-center gap-3 py-2 px-3 rounded-md border border-border bg-card">
            <Icon name="folder" className="h-5 w-5 flex-shrink-0 text-muted-foreground" />
            <div className="flex flex-col flex-1 min-w-0">
              <input
                ref={newFolderInputRef}
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void handleCreateFolder()
                  if (e.key === 'Escape') { setIsCreatingFolder(false); setNewFolderName('') }
                }}
                onBlur={() => { if (!newFolderName.trim()) setIsCreatingFolder(false) }}
                placeholder="Folder name"
                className="bg-transparent text-sm font-medium outline-none placeholder:text-muted-foreground"
              />
              <span className="text-xs text-muted-foreground">0 models</span>
            </div>
            <button
              onClick={() => void handleCreateFolder()}
              className="flex h-5 w-5 items-center justify-center text-muted-foreground hover:text-foreground"
            >
              <Icon name="check" className="h-4 w-4" />
            </button>
            <button
              onClick={() => { setIsCreatingFolder(false); setNewFolderName('') }}
              className="flex h-5 w-5 items-center justify-center text-muted-foreground hover:text-foreground"
            >
              <Icon name="x" className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Folder Items (hidden when inside a folder) */}
        {!openFolderId && folders.map((folder) => (
          <div
            key={folder.id}
            className="flex items-center gap-3 py-2 px-3 rounded-md border border-border bg-card hover:border-foreground/20 cursor-pointer group transition-colors"
            onClick={() => setOpenFolderId(folder.id)}
          >
            <Icon name="folder" className="h-5 w-5 flex-shrink-0" />
            <div className="flex flex-col flex-1 min-w-0">
              {renamingFolderId === folder.id ? (
                <input
                  ref={renameFolderInputRef}
                  type="text"
                  value={renameFolderName}
                  onChange={(e) => setRenameFolderName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void handleFolderRenameSubmit(folder.id)
                    if (e.key === 'Escape') setRenamingFolderId(null)
                  }}
                  onBlur={() => void handleFolderRenameSubmit(folder.id)}
                  onClick={(e) => e.stopPropagation()}
                  className="bg-transparent text-sm font-medium outline-none border-b border-border w-full"
                />
              ) : (
                <span className="text-sm font-medium truncate">{folder.name}</span>
              )}
              <span className="text-xs text-muted-foreground">
                {folder.loraCount} {folder.loraCount === 1 ? 'model' : 'models'}
              </span>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex h-5 w-5 items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Icon name="more-horizontal" className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                <DropdownMenuItem onClick={() => startFolderRename(folder)}>
                  <MenuIcon name="pencil" className="h-4 w-4 mr-2" />
                  Rename
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => void deleteFolder(folder.id)}
                  className="text-destructive focus:text-destructive"
                >
                  <MenuIcon name="trash-2" className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ))}

        {/* Models */}
        {models.length === 0 && folders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
            <Icon name="box" className="h-8 w-8 mb-2 opacity-40" />
            <p className="text-sm font-medium">No models yet</p>
            <p className="text-xs mt-1 opacity-70">Train or import a LoRA model.</p>
          </div>
        ) : filteredModels.length === 0 && !openFolderId && folders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
            <Icon name="search" className="h-8 w-8 mb-2 opacity-40" />
            <p className="text-sm font-medium">No matches</p>
            <p className="text-xs mt-1 opacity-70">Try a different search term.</p>
          </div>
        ) : filteredModels.length === 0 && openFolderId ? (
          <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
            <Icon name="folder" className="h-8 w-8 mb-2 opacity-40" />
            <p className="text-sm font-medium">Folder is empty</p>
            <p className="text-xs mt-1 opacity-70">Move models here from the context menu.</p>
          </div>
        ) : (
          filteredModels.map((model) => (
            <DraggableModelItem
              key={model.id}
              model={model}
              folders={folders}
              isRenaming={renamingId === model.id}
              renameValue={renameValue}
              onRenameValueChange={setRenameValue}
              onRenameSubmit={() => void handleRenameSubmit()}
              onRenameCancel={handleRenameCancel}
              onStartRename={() => handleStartRename(model)}
              onDelete={() => void handleDelete(model.id)}
              onMoveToFolder={async (folderId) => {
                const success = await moveLoraToFolder(model.id, folderId)
                if (!success) toast.error('Failed to move model')
              }}
            />
          ))
        )}
      </div>
    </div>
  )
}
